import {Component, SecurityContext, Input} from '@angular/core';
import {Subject} from 'rxjs';
import {Project} from 'src/app/models/project';
import {AiReportSummary, AiReportSummaryStatus, AiReportSummaryStatusResponse} from 'src/app/models/ai-report-summary';
import {SpModalInfoComponent, ModalData} from 'src/app/modules/sp-modal/sp-modal-info/sp-modal-info.component';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DomSanitizer} from '@angular/platform-browser';
import {marked} from 'marked';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';

import {TitanButtonComponent} from 'src/app/user-interface/titan-button/titan-button.component';
import {LoaderModalComponent} from 'src/app/modules/sp-modal/loader-modal/loader-modal.component';
import {EmbeddedLoaderComponent} from '../../embedded-components/embedded-loader.component';
import {EmbeddedMessageComponent} from '../../embedded-components/embedded-message.component';
import {
  ApiAiReportSummaryService
} from 'src/app/services/sp-api/sp-api-ai-report-summary/api-ai-report-summary.service';
import {TrackerService} from 'src/app/services/sp-tracker/tracker.service';
import {ExportService} from 'src/app/services/sp-export/export.service';
import {AlertService} from 'src/app/services/sp-alert/alert.service';
import { LoggerService } from 'src/app/services/sp-logger/logger.service';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';

export enum DisplayMode {
  Modal,
  TextOnly
}

@Component({
  selector: 'app-ai-report-summary',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    TitanButtonComponent,
    EmbeddedLoaderComponent,
    EmbeddedMessageComponent
  ],
  templateUrl: './ai-report-summary.component.html',
  styleUrls: ['./ai-report-summary.component.scss']
})


export class AiReportSummaryComponent {
  DisplayMode = DisplayMode;
  @Input() displayMode: DisplayMode;

  private _project: Project;
  @Input() set project(value: Project) {
    const previousProjectId = this._project?.id;
    this._project = value;
    // Only re-initialize when the selected project actually changes by id.
    // Behaviors reassign projectManager.selectedProject on every route change, which
    // would otherwise reset isLoading and visually drop the button between tabs of
    // the same project.
    if (previousProjectId !== undefined && value && previousProjectId !== value.id) {
      this.resetSummaryState();
      this.initializeSummary();
    }
  }
  get project(): Project {
    return this._project;
  }

  @Input() contentOveride: string;
  @Input() offline: boolean;

  private readonly insightsSummaryStatusCheckInterval = 5000;

  public readonly AiReportSummaryStatus = AiReportSummaryStatus;
  private loaderDialogRef: MatDialogRef<LoaderModalComponent> | null = null;

  private insightsSummaryCheckInterval?: NodeJS.Timeout;
  private dataReadyCheckInterval?: NodeJS.Timeout;

  protected insightsSummaryStatus: AiReportSummaryStatus | null;
  protected insightsSummaryContent: string;
  protected isLoading = true;
  protected isExporting = false;

  loaderDialogTitle = 'Your summary is being generated';
  loaderDialogContent = 'Our AI assistant is putting the pieces together.';
  loaderDialogButtonText = 'Close';
  loaderDialogImage = 'assets/images/icons/soprism/Ai.svg';
  loaderDialogCountdown = 60;
  loaderDialogCountdownText = 'We estimate your Insights Summary to be ready in less than ';

  areProjectDataReady = false;


  errorTitle = 'Data Not Ready';
  errorMessage = 'The data needed to generate the Insights Summary for this report is not ready yet.';
  errorActionText = 'Please contact our support team for assistance.';

  private rollbarErrorAlreadySent = false;

  constructor(
    private apiInsightsSummaryService: ApiAiReportSummaryService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private tracker: TrackerService,
    private exportService: ExportService,
    private alert: AlertService,
    protected sessionProvider: SessionProviderService,
    private loggerService: LoggerService) {
  }

  private resetSummaryState() {
    this.stopInsightsSummaryCheckLoop();
    this.clearDataReadyCheck();
    this.insightsSummaryStatus = null;
    this.insightsSummaryContent = '';
    this.areProjectDataReady = false;
    this.rollbarErrorAlreadySent = false;
    this.isLoading = true;
    this.isExporting = false;
  }

  async ngOnInit() {
    await this.initializeSummary();
  }

  private async initializeSummary() {
    this.isLoading = true;

    if (this.offline) {
      if (this.contentOveride) {
        this.insightsSummaryContent = this.contentOveride;
        this.areProjectDataReady = true;
        this.insightsSummaryStatus = AiReportSummaryStatus.COMPLETED;
      } else {
        this.areProjectDataReady = false;
        this.errorTitle = 'Not Available';
        this.errorMessage = 'The Insights Summary is not available for this report.';
        this.errorActionText = '';
      }

    } else {
      this.startDataReadyCheck();
      const insightsSummaryStatusResponse = await this.getAndDefineAiReportSummaryStatus();
      this.insightsSummaryStatus = insightsSummaryStatusResponse.status;
      if (this.insightsSummaryStatus === AiReportSummaryStatus.COMPLETED) {
        await this.getAndDefineInsightsSummary();
      } else if (this.insightsSummaryStatus === AiReportSummaryStatus.GENERATING || this.insightsSummaryStatus === AiReportSummaryStatus.NOT_STARTED) {
        this.startInsightsSummaryCheckLoop();
      }

      if (this.displayMode === DisplayMode.TextOnly && this.insightsSummaryStatus === null) {
        this.startInsightsSummaryGeneration();
      }
    }

    this.isLoading = false;
  }

  ngOnDestroy() {
    this.stopInsightsSummaryCheckLoop();
    this.clearDataReadyCheck();
  }

  private startInsightsSummaryCheckLoop() {
    this.stopInsightsSummaryCheckLoop();

    this.insightsSummaryCheckInterval = setInterval(() => {
      this.getAndDefineAiReportSummaryStatus().then(async insightsSummaryStatus => {
        if (insightsSummaryStatus.status === AiReportSummaryStatus.COMPLETED) {
          this.stopInsightsSummaryCheckLoop();
          await this.getAndDefineInsightsSummary();
          if (this.loaderDialogRef) {
            this.loaderDialogRef.close();
            this.loaderDialogRef = null;
          }
        }
      });
    }, this.insightsSummaryStatusCheckInterval);
  }

  private stopInsightsSummaryCheckLoop() {
    if (this.insightsSummaryCheckInterval) {
      clearInterval(this.insightsSummaryCheckInterval);
      this.insightsSummaryCheckInterval = undefined;
    }
  }

  private startDataReadyCheck() {
    if (!this.project) { return; }
    this.clearDataReadyCheck();

    if (this.displayMode === DisplayMode.TextOnly) {
      this.checkDataReadyOnce();
    } else {
      this.dataReadyCheckInterval = setInterval(async () => {
        this.areProjectDataReady = await this.apiInsightsSummaryService.canCreateAiReportSummary(this.project.id);
        if (this.areProjectDataReady) {
          this.clearDataReadyCheck();
        }
      }, 1000);
    }
  }

  private async checkDataReadyOnce() {
    if (!this.project) { return; }
    this.areProjectDataReady = await this.apiInsightsSummaryService.canCreateAiReportSummary(this.project.id);

    if (!this.areProjectDataReady && !this.rollbarErrorAlreadySent) {
      this.reportDataNotReadyError();
      this.rollbarErrorAlreadySent = true;
    }
  }

  private clearDataReadyCheck() {
    if (this.dataReadyCheckInterval) {
      clearInterval(this.dataReadyCheckInterval);
      this.dataReadyCheckInterval = undefined;
    }
  }

  async startInsightsSummaryGeneration() {
    this.startInsightsSummaryCheckLoop();

    await this.generateInsightsSummary();
    await this.getAndDefineAiReportSummaryStatus();

    if (this.displayMode !== DisplayMode.TextOnly) {
      this.tracker.track('ai_report_summary_generation_started', {
        report_id: this.project.id,
        type: 'insights-summary'
      });
      this.showInsightsSummaryLoaderDialog();
    } else {
      this.tracker.track('ai_widget_report_summary_generation_started', {
        report_id: this.project.id,
        type: 'insights-summary'
      });
    }

  }

  private async generateInsightsSummary() {
    await this.apiInsightsSummaryService.generateAiReportSummary(this.project.id, 'insights-summary');
  }

  async getAndDefineInsightsSummary(): Promise<AiReportSummary | null> {
    try {
      const insightsSummary = await this.apiInsightsSummaryService.getAiReportSummary(this.project.id, 'insights-summary');

      this.insightsSummaryStatus = insightsSummary.status as AiReportSummaryStatus;
      this.insightsSummaryContent = insightsSummary.text_output;
      this.project._insights_summary = insightsSummary;


      return insightsSummary;
    } catch (error) {
      // TODO: Notify user that the insights summary is not available ?
      return null;
    }
  }

  async getAndDefineAiReportSummaryStatus(): Promise<AiReportSummaryStatusResponse> {
    if (!this.project) return {status: null, remaining_seconds: 0};
    try {
      const insightsSummary = await this.apiInsightsSummaryService.getAiReportSummary(this.project.id, 'insights-summary');
      this.insightsSummaryStatus = insightsSummary.status as AiReportSummaryStatus;
      this.loaderDialogCountdown = insightsSummary.remaining_seconds;
      return {status: this.insightsSummaryStatus, remaining_seconds: this.loaderDialogCountdown};
    } catch (error) {
      this.insightsSummaryStatus = null;
      return {status: null, remaining_seconds: 0};
    }
  }


  private showInsightsSummaryLoaderDialog(): void {
    this.loaderDialogRef = this.dialog.open(LoaderModalComponent, {
      data: {
        title: this.loaderDialogTitle,
        content: this.loaderDialogContent,
        buttonText: this.loaderDialogButtonText,
        image: this.loaderDialogImage,
        countdown: this.loaderDialogCountdown,
        countdownText: this.loaderDialogCountdownText
      },
      width: '400px',
      autoFocus: false,
    });

    this.loaderDialogRef.afterClosed().subscribe(() => {
      if (this.insightsSummaryStatus === AiReportSummaryStatus.COMPLETED) {
        this.showInsightsSummaryDialog();
      }
    });
  }

  public showInsightsSummaryDialog(): void {
    this.tracker.track('ai_report_summary_content_viewed', {
      report_id: this.project.id,
      type: 'insights-summary'
    });

    // Create a Subject to communicate the exporting state
    const isExecutingAction$ = new Subject<boolean>();

    this.dialog.open(SpModalInfoComponent, {
      data: {
        title: 'Insights Summary - ' + this.project.name,
        content: this.parsedInsightsSummaryWithoutTitle,
        buttonText: 'Close',
        primaryButtonFunction: async () => {
          isExecutingAction$.next(true);
          await this.exportInsightsSummary();
          isExecutingAction$.next(false);
        },
        primaryButtonText: 'Export to PDF',
        actionText: 'Exporting your Insights Summary...',
        isExecutingAction$: isExecutingAction$
      },
      width: '800px',
      panelClass: 'ai-report-summary-dialog',
      autoFocus: false
    });
  }

  get parsedInsightsSummary(): string {
    if (!this.insightsSummaryContent) {
      return '';
    }

    const parsedMarkdown = marked.parse(this.insightsSummaryContent);
    return this.sanitizer.sanitize(SecurityContext.HTML, parsedMarkdown) || '';
  }

  get parsedInsightsSummaryWithoutTitle(): string {
    if (!this.insightsSummaryContent) {
      return '';
    }

    // Remove the first H1 from the content to avoid duplication
    const contentWithoutFirstH1 = this.insightsSummaryContent.replace(/^#\s+.+$/m, '').trim();
    const parsedMarkdown = marked.parse(contentWithoutFirstH1);
    return this.sanitizer.sanitize(SecurityContext.HTML, parsedMarkdown) || '';
  }

  private reportDataNotReadyError(): void {
    this.loggerService.logError('Data not ready for Insights Summary generation', 2);
  }

  async exportInsightsSummary() {
    if (this.isExporting) return;

    this.isExporting = true;

    this.tracker.track('ai_report_summary_export_started', {
      report_id: this.project.id,
      type: 'insights-summary'
    });

    try {
      // Use basic CSS for PDF export - the dialog styling is handled by the SCSS file
      const basicCss = `
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          font-weight: 400;
          line-height: 1.5;
          color: #484848;
          margin: 0;
        }

         h5 {
          font-size: 28px !important;
          font-weight: 700 !important;
          text-align: left;
          align-self: flex-start !important;
          margin: 0;
        }


        /* Style h3 headings within the content (subtitles) */
        h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 20px 0 12px;
          text-align: left;
        }

        /* Style other headings within the content */
        h1, h2 {
          font-size: 20px;
          font-weight: 600;
          margin: 18px 0 10px;
          text-align: left;
        }


        strong {
          color: #EF8251;
          font-weight: 700;
        }


      `;

      const htmlWithTitle = `<h5>Insights Summary - ${this.project.name}</h5>${this.parsedInsightsSummaryWithoutTitle}`;
      await this.exportService.exportHtmlToPdf(htmlWithTitle, basicCss, `Insights_Summary_${this.project.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    } catch (error) {
      console.error('Export failed:', error);
      this.alert.notify('Error', 'Failed to export PDF. Please try again.', 'error');
    } finally {
      this.isExporting = false;
    }
  }


}
